import React from "react";
import { X } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { initials as toInitials } from "@/lib/theme";

export function GoogleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export function Spinner({ style }) {
  return <div className="spinner" style={style} />;
}

export function PrimaryButton({ label, onClick, disabled, loading, color = "#8B7CF6", textColor = "#FFFFFF", icon: Icon, testID, style, className = "" }) {
  const off = disabled || loading;
  return (
    <button
      data-testid={testID}
      onClick={onClick}
      disabled={off}
      className={`w-full rounded-xl py-3.5 font-display font-bold text-[15px] transition-all active:scale-[0.98] flex items-center justify-center gap-2 ${className}`}
      style={{ background: color, color: textColor, opacity: off ? 0.35 : 1, cursor: off ? "not-allowed" : "pointer", ...style }}
    >
      {loading ? <Spinner style={{ borderTopColor: textColor }} /> : (<>{Icon && <Icon size={18} />}{label}</>)}
    </button>
  );
}

export function GhostButton({ label, onClick, color = "#A594FF", testID }) {
  return (
    <button data-testid={testID} onClick={onClick} className="w-full py-3 font-display font-medium text-sm transition-opacity hover:opacity-70" style={{ color }}>
      {label}
    </button>
  );
}

export function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="font-display font-medium text-[11.5px] uppercase tracking-wider mb-1.5" style={{ color: "#6B6B7D" }}>{label}</div>
      {children}
    </div>
  );
}

export function Input({ testID, className = "", ...props }) {
  return (
    <input
      data-testid={testID}
      {...props}
      className={`w-full rounded-xl px-4 py-3 font-display text-[15px] transition-colors focus:border-[#8B7CF6] ${className}`}
      style={{ background: "#2C2D3A", border: "1px solid #2C2D3A", color: "#F8F9FA" }}
    />
  );
}

export function Textarea({ testID, className = "", ...props }) {
  return (
    <textarea
      data-testid={testID}
      {...props}
      className={`w-full rounded-xl px-4 py-3 font-display text-[15px] transition-colors focus:border-[#8B7CF6] resize-none ${className}`}
      style={{ background: "#2C2D3A", border: "1px solid #2C2D3A", color: "#F8F9FA", minHeight: 76 }}
    />
  );
}

export function Avatar({ name, uri, size = 40, color = "#322B59", textColor = "#A594FF" }) {
  const resolved = mediaUrl(uri);
  return (
    <div className="flex items-center justify-center overflow-hidden shrink-0" style={{ width: size, height: size, borderRadius: size / 2, background: color }}>
      {resolved ? (
        <img src={resolved} alt={name || ""} style={{ width: size, height: size, objectFit: "cover" }} />
      ) : (
        <span className="font-display font-bold" style={{ color: textColor, fontSize: size * 0.36 }}>{toInitials(name)}</span>
      )}
    </div>
  );
}

export function Chip({ label, icon: Icon, active, onClick, color = "#8B7CF6", testID }) {
  return (
    <button
      data-testid={testID}
      onClick={onClick}
      className="flex items-center gap-1.5 rounded-full py-2 px-3.5 font-display font-medium text-[12.5px] transition-all shrink-0"
      style={{
        background: active ? color + "26" : "#20212C",
        border: `1px solid ${active ? color : "#2C2D3A"}`,
        color: active ? color : "#9CA3AF",
      }}
    >
      {Icon && <Icon size={14} />}
      {label}
    </button>
  );
}

export function Sheet({ visible, onClose, title, children, testID }) {
  if (!visible) return null;
  return (
    <div data-testid={testID} className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div className="absolute inset-0 animate-fade-in" style={{ background: "rgba(8,8,12,0.72)" }} onClick={onClose} data-testid="sheet-backdrop" />
      <div
        className="relative w-full md:max-w-lg animate-slide-up md:rounded-2xl rounded-t-2xl px-4 pt-2 pb-6 max-h-[90vh] overflow-y-auto no-scrollbar"
        style={{ background: "#20212C", border: "1px solid #2C2D3A" }}
      >
        <div className="w-10 h-1 rounded-full mx-auto mb-3 md:hidden" style={{ background: "#4A4B5C" }} />
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg" style={{ color: "#F8F9FA" }}>{title}</h2>
          <button data-testid="sheet-close" onClick={onClose} className="p-1.5 rounded-md transition-colors hover:bg-[#2C2D3A]" style={{ background: "#2C2D3A", border: "1px solid #2C2D3A" }}>
            <X size={18} color="#A594FF" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Toggle({ on, onChange, testID, color = "#8B7CF6" }) {
  return (
    <button
      data-testid={testID}
      onClick={() => onChange(!on)}
      className="relative inline-flex items-center rounded-full transition-colors shrink-0"
      style={{ width: 42, height: 24, background: on ? color : "#2C2D3A" }}
    >
      <span className="absolute rounded-full bg-white transition-transform" style={{ width: 18, height: 18, top: 3, left: 3, transform: on ? "translateX(18px)" : "translateX(0)" }} />
    </button>
  );
}

export function FullPageSpinner() {
  return (
    <div className="flex items-center justify-center h-full w-full" style={{ minHeight: "60vh" }}>
      <Spinner />
    </div>
  );
}
